import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAllSavedItems, addBoardItem, removeBoardItem, getBoards, getOrCreateDefaultBoard, getBoardPickerOptions, createBoard, getBoardItems } from '../api/boardApi';
import { useAuth } from './AuthContext';

const SavedItemsContext = createContext(null);

/**
 * savedMap: { [targetId]: { archiveItemId, boardColor } }
 * 로그인 시 1회 fetch → 저장/해제는 낙관적 업데이트로 로컬 상태만 변경
 */
export function SavedItemsProvider({ children }) {
  const { isLoggedIn, role } = useAuth();
  const [castingMap, setCastingMap] = useState({});
  const [talentMap, setTalentMap] = useState({});
  const [boardPickerState, setBoardPickerState] = useState(null);
  const [savedConfirmState, setSavedConfirmState] = useState(null); // { boardName, targetType }
  // boardId → color 매핑 캐시
  const [boardColorCache, setBoardColorCache] = useState({});

  useEffect(() => {
    if (!isLoggedIn) {
      setCastingMap({});
      setTalentMap({});
      setBoardColorCache({});
      return;
    }
    Promise.all([
      getAllSavedItems().catch(() => []),
      getBoards().catch(() => []),
    ]).then(([items, boards]) => {
      // boardId → color 캐시 구성
      const colorCache = {};
      (boards || []).forEach(b => {
        if (b.id != null) colorCache[String(b.id)] = b.color || null;
      });
      setBoardColorCache(colorCache);

      const cMap = {};
      const tMap = {};
      (Array.isArray(items) ? items : (items?.content ?? []))
        .forEach(item => {
          const boardColor = item.boardId != null ? (colorCache[String(item.boardId)] || null) : null;
          if (item.targetType === 'CASTING_POST') {
            cMap[String(item.targetId)] = { archiveItemId: item.id, boardColor };
          } else if (item.targetType === 'TALENT_PROFILE') {
            tMap[String(item.targetId)] = { archiveItemId: item.id, boardColor };
          }
        });
      setCastingMap(cMap);
      setTalentMap(tMap);
    });
  }, [isLoggedIn]);

  const getSavedMap = useCallback((targetType) => {
    return targetType === 'CASTING_POST' ? castingMap : talentMap;
  }, [castingMap, talentMap]);

  const setMap = useCallback((targetType, updater) => {
    if (targetType === 'CASTING_POST') setCastingMap(updater);
    else setTalentMap(updater);
  }, []);

  const toggleSave = useCallback(async (targetId, targetType) => {
    // picker가 이미 열려있으면 중복 실행 방지
    if (boardPickerState) return;
    const map = targetType === 'CASTING_POST' ? castingMap : talentMap;
    const entry = map[String(targetId)];

    if (entry) {
      // 낙관적 삭제
      setMap(targetType, prev => {
        const next = { ...prev };
        delete next[String(targetId)];
        return next;
      });
      removeBoardItem(entry.archiveItemId).catch(() => {
        setMap(targetType, prev => ({ ...prev, [String(targetId)]: entry }));
      });
    } else {
      try {
        const boards = await getBoards();
        if (!boards.length) {
          const boardId = await getOrCreateDefaultBoard();
          const result = await addBoardItem({ boardId, targetType, targetId: Number(targetId) });
          const boardColor = boardColorCache[String(boardId)] || null;
          setMap(targetType, prev => ({ ...prev, [String(targetId)]: { archiveItemId: result?.id ?? true, boardColor } }));
          return;
        }
        const preferredKey = targetType === 'TALENT_PROFILE'
          ? 'producer-bookmark-preferred-default'
          : 'talent-archive-preferred-default';
        const preferredDefaultBoardId = (() => { try { return localStorage.getItem(preferredKey); } catch { return null; } })();
        const options = await getBoardPickerOptions({ targetType, preferredDefaultBoardId });
        setBoardPickerState({ targetId: String(targetId), targetType, options });
      } catch {}
    }
  }, [castingMap, talentMap, setMap, boardColorCache]);

  // 같은 targetId가 다른 보관함에 이미 저장된 경우 모두 삭제
  const removeAllExistingItems = useCallback(async (targetId, targetType) => {
    try {
      const allItems = await getAllSavedItems();
      const list = Array.isArray(allItems) ? allItems : (allItems?.content ?? []);
      const duplicates = list.filter(
        item => item.targetType === targetType && String(item.targetId) === String(targetId)
      );
      await Promise.all(duplicates.map(item => removeBoardItem(item.id)));
    } catch {}
  }, []);

  const selectBoard = useCallback(async (boardId) => {
    if (!boardPickerState) return;
    const { targetId, targetType } = boardPickerState;
    const selectedBoard = boardPickerState.options?.find(b => String(b.id) === String(boardId));
    const boardName = selectedBoard?.name || '보관함';
    try {
      await removeAllExistingItems(targetId, targetType);
      const result = await addBoardItem({ boardId, targetType, targetId: Number(targetId) });
      const boardColor = selectedBoard?.color || boardColorCache[String(boardId)] || null;
      setMap(targetType, prev => ({ ...prev, [String(targetId)]: { archiveItemId: result?.id ?? true, boardColor } }));
      setBoardPickerState(null);
      setSavedConfirmState({ boardName, boardId: String(boardId), targetType });
    } catch {
      setBoardPickerState(null);
    }
  }, [boardPickerState, setMap, boardColorCache, removeAllExistingItems]);

  const createAndSelectBoard = useCallback(async (boardData) => {
    if (!boardPickerState) return;
    const { targetId, targetType } = boardPickerState;
    try {
      await removeAllExistingItems(targetId, targetType);
      const newBoard = await createBoard(boardData);
      const result = await addBoardItem({ boardId: newBoard.id, targetType, targetId: Number(targetId) });
      const boardColor = newBoard.color || boardData.color || null;
      setBoardColorCache(prev => ({ ...prev, [String(newBoard.id)]: boardColor }));
      setMap(targetType, prev => ({ ...prev, [String(targetId)]: { archiveItemId: result?.id ?? true, boardColor } }));
      setBoardPickerState(null);
      setSavedConfirmState({ boardName: boardData.name, boardId: String(newBoard.id), targetType });
    } catch {
      setBoardPickerState(null);
    }
  }, [boardPickerState, setMap, removeAllExistingItems]);

  const closeBoardPicker = useCallback(() => setBoardPickerState(null), []);
  const closeSavedConfirm = useCallback(() => setSavedConfirmState(null), []);

  // 외부에서 직접 map 항목 제거 (보관함 페이지 삭제 후 동기화용)
  const removeFromMap = useCallback((targetId, targetType) => {
    setMap(targetType, prev => {
      const next = { ...prev };
      delete next[String(targetId)];
      return next;
    });
  }, [setMap]);

  return (
    <SavedItemsContext.Provider value={{
      castingMap,
      talentMap,
      getSavedMap,
      toggleSave,
      boardPickerState,
      savedConfirmState,
      selectBoard,
      createAndSelectBoard,
      closeBoardPicker,
      closeSavedConfirm,
      removeFromMap,
    }}>
      {children}
    </SavedItemsContext.Provider>
  );
}

export function useSavedItems() {
  const ctx = useContext(SavedItemsContext);
  if (!ctx) throw new Error('useSavedItems must be used within SavedItemsProvider');
  return ctx;
}
